/**
 * DashboardLab — Refonte UI/UX niveau ParcoursList + ParcoursDetails
 * Même système de tokens T, DM Mono + Georgia, CausalGrid, HUD rails, coins angulaires.
 * Toutes les fonctionnalités d'origine conservées intactes.
 */

import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  ArrowRight,
  Bell,
  BookOpen,
  CheckCircle,
  ChevronRight,
  Clock,
  Database,
  Gamepad,
  LogOut,
  Menu,
  Plus,
  Sparkles,
  Terminal,
  ToolCase,
  TrendingUp,
  Trophy,
  X,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingModal from '../components/DashboardLearner/OnBoarding';
import { MessageriePanel, useMessagrieBadge } from '../components/MessageriePanel';
import api from '../config/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { useUser } from '../contexts/UserContext';
import { useAtelierUnlock } from '../hooks/useAtelierUnlock';
import { parcoursService } from '../services/parcoursService';
import { usePesaParcours } from '../stores/usePesaParcours';

/* ── DESIGN TOKENS (identiques ParcoursList / ParcoursDetails) ── */
const T = {
  bg:       '#050a0e',
  bgPanel:  '#080d12',
  bgCard:   '#0b1219',
  border:   '#1a2a38',
  accent:   '#e07b20',
  accentG:  '#f59e0b',
  cyan:     '#0ea5e9',
  cyanG:    '#38bdf8',
  green:    '#10b981',
  orange:   '#ff5a28',
  purple:   '#8b5cf6',
  danger:   '#f87171',
  text:     '#f7f7f7',
  textDim:  'rgba(250,250,250,0.93)',
  textMute: 'rgba(255, 255, 255, 0.70)',
  scan:     'rgba(14,165,233,0.018)',
};

/* ── PRIMITIVES ─────────────────────────────────────────────── */
const Mono = ({ children, size = 11, color = T.textMute, w = false, style = {} }) => (
  <span style={{ fontFamily: "'DM Mono','Roboto Mono',monospace", fontSize: size, letterSpacing: '0.06em', color, fontWeight: w ? 700 : 400, lineHeight: 1, ...style }}>
    {children}
  </span>
);

const HudCorners = ({ color1 = T.cyan, color2 = T.accent, size = 7 }) => (
  <>
    <span style={{ position: 'absolute', top: -1, left: -1, width: size, height: size, borderTop: `1px solid ${color1}`, borderLeft: `1px solid ${color1}`, zIndex: 5 }} />
    <span style={{ position: 'absolute', bottom: -1, right: -1, width: size, height: size, borderBottom: `1px solid ${color2}`, borderRight: `1px solid ${color2}`, zIndex: 5 }} />
  </>
);

const CardScanLine = ({ color = T.cyan }) => (
  <motion.div
    animate={{ top: ['-5%', '105%'], opacity: [0, 0.45, 0] }}
    transition={{ duration: 5, repeat: Infinity, ease: 'linear', repeatDelay: 4 }}
    style={{ position: 'absolute', left: 0, right: 0, height: 1, pointerEvents: 'none', zIndex: 20, background: `linear-gradient(90deg,transparent,${color}50,${T.accentG}35,transparent)` }}
  />
);

const LiveClock = () => {
  const [t, setT] = useState('');
  useEffect(() => {
    const tick = () => setT(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, []);
  return <span>{t}</span>;
};

/* ── GRILLE CAUSALE ─────────────────────────────────────────── */
const CausalGrid = ({ activeColor }) => {
  const cols = 11, rows = 8, nodes = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++)
      nodes.push({ id: `${r}-${c}`, x: (c / (cols - 1)) * 100, y: (r / (rows - 1)) * 100, delay: (r + c) * 0.06 });
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {Array.from({ length: rows }).map((_, r) => (
        <motion.div key={`h${r}`} animate={{ opacity: activeColor ? [0.04, 0.12, 0.04] : [0.015, 0.035, 0.015] }} transition={{ duration: 4 + r, repeat: Infinity, delay: r * 0.3 }}
          style={{ position: 'absolute', left: 0, right: 0, top: `${(r / (rows - 1)) * 100}%`, height: 1, background: `linear-gradient(90deg,transparent,${activeColor || T.cyan}40,transparent)` }} />
      ))}
      {Array.from({ length: cols }).map((_, c) => (
        <motion.div key={`v${c}`} animate={{ opacity: activeColor ? [0.04, 0.12, 0.04] : [0.015, 0.035, 0.015] }} transition={{ duration: 5 + c * 0.4, repeat: Infinity, delay: c * 0.25 }}
          style={{ position: 'absolute', top: 0, bottom: 0, left: `${(c / (cols - 1)) * 100}%`, width: 1, background: `linear-gradient(180deg,transparent,${activeColor || T.accent}28,transparent)` }} />
      ))}
      {nodes.map(n => (
        <motion.div key={n.id} animate={{ opacity: activeColor ? [0.1, 0.45, 0.1] : [0.015, 0.1, 0.015], scale: [0.8, 1, 0.8] }} transition={{ duration: 3 + Math.random() * 2, repeat: Infinity, delay: n.delay }}
          style={{ position: 'absolute', left: `${n.x}%`, top: `${n.y}%`, width: 3, height: 3, borderRadius: '50%', background: activeColor || T.cyan, transform: 'translate(-50%,-50%)' }} />
      ))}
    </div>
  );
};

/* ── RING ───────────────────────────────────────────────────── */
const Ring = ({ value, size = 72, stroke = 3, color }) => {
  const r = size / 2 - stroke - 2;
  const c2 = 2 * Math.PI * r;
  const off = c2 - (value / 100) * c2;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeLinecap="square" strokeDasharray={c2} strokeDashoffset={off}
        style={{ transform: 'rotate(-90deg)', transformOrigin: `${size / 2}px ${size / 2}px`, transition: 'stroke-dashoffset 1.8s cubic-bezier(.4,0,.2,1)', filter: `drop-shadow(0 0 5px ${color}80)` }} />
    </svg>
  );
};

/* ── ANIMATED NUMBER ────────────────────────────────────────── */
const AnimNum = ({ to, duration = 1.4 }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    let s = 0; const end = parseInt(to) || 0;
    if (!end) { setV(0); return; }
    const step = Math.ceil(end / (duration * 60));
    const t = setInterval(() => { s += step; if (s >= end) { setV(end); clearInterval(t); } else setV(s); }, 1000 / 60);
    return () => clearInterval(t);
  }, [to, duration]);
  return <>{v}</>;
};

/* ── RANK ───────────────────────────────────────────────────── */
const getRank = s => {
  if (s >= 80) return { label: 'Expert',       color: T.accentG };
  if (s >= 60) return { label: 'Avancé',        color: T.cyan   };
  if (s >= 40) return { label: 'Intermédiaire', color: T.green  };
  if (s >= 20) return { label: 'Confirmé',      color: T.accent };
  return              { label: 'Apprenti',       color: T.text };
};

/* ── AVATAR ─────────────────────────────────────────────────── */
const Avatar = ({ user, size = 30 }) => {
  const ini = `${user?.nom?.[0] || ''}${user?.prenom?.[0] || ''}`.toUpperCase();
  return (
    <div style={{ width: size, height: size, flexShrink: 0, position: 'relative', border: `1px solid ${T.cyan}30`, background: `${T.cyan}06`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.36, fontWeight: 700, color: T.cyan, fontFamily: "'DM Mono',monospace" }}>
      <HudCorners color1={`${T.cyan}55`} color2={`${T.accent}55`} size={4} />
      {user?.photoUrl ? <img src={user.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (ini || '?')}
    </div>
  );
};

/* ── USEISMOBILE ────────────────────────────────────────────── */
const useIsMobile = (bp = 900) => {
  const [m, setM] = useState(() => window.innerWidth < bp);
  useEffect(() => { const fn = () => setM(window.innerWidth < bp); window.addEventListener('resize', fn); return () => window.removeEventListener('resize', fn); }, [bp]);
  return m;
};

/* ─────────────────────────────────────────────────────────────
   HERO — terminal de session
───────────────────────────────────────────────────────────── */
const Hero = ({ user, stats }) => {
  const rank = getRank(stats.confiance);
  const nextMap = { Expert: null, Avancé: 80, Intermédiaire: 60, Confirmé: 40, Apprenti: 20 };
  const prevMap = { Expert: 80, Avancé: 60, Intermédiaire: 40, Confirmé: 20, Apprenti: 0 };
  const next = nextMap[rank.label];
  const prev = prevMap[rank.label] || 0;
  const pct = next ? Math.round(((stats.confiance - prev) / (next - prev)) * 100) : 100;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
      style={{ position: 'relative', background: `linear-gradient(145deg, ${T.bgCard}, ${T.bgPanel})`, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden', padding: '1.4rem 1.6rem' }}>
      {/* Scan-lines fond */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`, pointerEvents: 'none' }} />
      <CardScanLine color={rank.color} />
      <HudCorners color1={`${rank.color}55`} color2={`${T.accent}55`} size={8} />

      {/* Glows ambiants */}
      <div style={{ position: 'absolute', top: -60, right: -40, width: 220, height: 220, background: `radial-gradient(circle, ${T.accentG}06 0%, transparent 65%)`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -50, left: -30, width: 160, height: 160, background: `radial-gradient(circle, ${T.cyan}05 0%, transparent 65%)`, pointerEvents: 'none' }} />

      <div style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: '1fr auto', gap: '1.5rem', alignItems: 'center' }}>

        {/* ── COL GAUCHE ── */}
        <div>
          {/* Eyebrow : statut session */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.8, repeat: Infinity }}
                style={{ width: 5, height: 5, borderRadius: '50%', background: T.green, boxShadow: `0 0 6px ${T.green}`, display: 'inline-block' }} />
              <Mono size={9} color={`${T.green}80`} style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>Session Active</Mono>
            </div>
            <span style={{ width: 1, height: 10, background: T.border }} />
            {/* Badge rank */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0.18rem 0.55rem', border: `1px solid ${rank.color}28`, background: `${rank.color}08` }}>
              <span style={{ width: 3, height: 3, borderRadius: '50%', background: rank.color }} />
              <Mono size={8} color={`${rank.color}90`} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>{rank.label}</Mono>
            </div>
            {/* Plan */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0.18rem 0.55rem', border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.02)' }}>
              <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>{user?.subscription?.plan?.toUpperCase() || 'FREEMIUM'}</Mono>
            </div>
          </div>

          {/* Titre — terminal readout */}
          <div style={{ marginBottom: '1.1rem' }}>
            <Mono size={9} color={T.textMute} style={{ display: 'block', marginBottom: '0.4rem', letterSpacing: '0.25em', textTransform: 'uppercase' }}>Bienvenue</Mono>
            <h2 style={{ fontFamily: "'Georgia', serif", fontWeight: 300, fontSize: 'clamp(1.8rem,3.5vw,3rem)', letterSpacing: '-0.02em', lineHeight: 1, margin: 0, color: T.text }}>
              <strong style={{ fontWeight: 700, background: `linear-gradient(90deg, ${T.accentG}, ${T.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {user?.prenom || user?.nom || 'Utilisateur'}
              </strong>
              
            </h2>
          </div>

          {/* Barre progression rank */}
          {next && (
            <div style={{ maxWidth: 300 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}>
                  Progression · {rank.label} → {Object.keys({ Avancé: 80, Intermédiaire: 60, Confirmé: 40, Apprenti: 20, Expert: null })[Object.values({ Avancé: 80, Intermédiaire: 60, Confirmé: 40, Apprenti: 20, Expert: null }).indexOf(next)]}
                </Mono>
                <Mono size={8} color={rank.color} w>{pct}%</Mono>
              </div>
              <div style={{ height: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 1, overflow: 'hidden' }}>
                <motion.div initial={{ width: 0 }} animate={{ width: `${Math.max(0, Math.min(100, pct))}%` }} transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
                  style={{ height: '100%', background: `linear-gradient(90deg, ${rank.color}, ${rank.color}bb)`, boxShadow: `0 0 8px ${rank.color}60`, borderRadius: 1 }} />
              </div>
              <Mono size={8} color={T.textMute} style={{ display: 'block', marginTop: '0.3rem' }}>
                {stats.confiance} pts · cible {next}
              </Mono>
            </div>
          )}
        </div>

        {/* ── COL DROITE : ring + stats ── */}
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <Ring value={stats.confiance} size={72} color={T.accentG} />
              <div style={{ position: 'absolute', textAlign: 'center' }}>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 18, fontWeight: 700, color: T.text, lineHeight: 1 }}>
                  <AnimNum to={stats.confiance} />
                </div>
                <Mono size={8} color={T.textMute}>SCORE</Mono>
              </div>
            </div>
            <Mono size={9} color={T.textMute} style={{ display: 'block', marginTop: '0.3rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Confiance</Mono>
          </div>

          {/* Bloc métriques */}
          <div style={{ position: 'relative', background: T.bgCard, border: `1px solid ${T.border}`, padding: '0.75rem 0.9rem', minWidth: 110 }}>
            <HudCorners color1={`${T.cyan}35`} color2={`${T.accent}35`} size={5} />
            {[
              { k: 'XP Total',  v: stats.xpTotal,          c: T.cyan   },
              { k: 'Modules',   v: stats.modulesCompleted,  c: T.accentG },
              { k: 'Certifs',   v: stats.certificates,      c: T.green  },
            ].map((row, i) => (
              <div key={row.k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.3rem 0', borderBottom: i < 2 ? `1px solid ${T.border}` : 'none' }}>
                <Mono size={8} color={T.textMute}>{row.k}</Mono>
                <Mono size={11} color={row.c} w><AnimNum to={row.v ?? 0} /></Mono>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────────────────────
   KPI STRIP
───────────────────────────────────────────────────────────── */
const KpiStrip = ({ stats }) => {
  const kpis = [
    { label: 'Modules Complétés',   v: stats.modulesCompleted, color: T.cyan   },
    { label: "Effort d'Acquisition", v: stats.xpTotal,          color: T.accentG },
    { label: 'Certifications',        v: stats.certificates,     color: T.green  },
    { label: 'Actifs Pesa',           v: stats.pesas,            color: T.accent },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1, background: T.border }}>
      {kpis.map((k, i) => (
        <motion.div key={k.label} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 + i * 0.05 }}
          style={{ position: 'relative', background: T.bgCard, padding: '1rem 1.1rem', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, ${k.color}60, transparent)` }} />
          <Mono size={8} color={T.textMute} style={{ display: 'block', marginBottom: '0.5rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>{k.label}</Mono>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 22, fontWeight: 700, color: T.text, lineHeight: 1 }}>
            <AnimNum to={k.v ?? 0} />
          </div>
          {/* Mini sparkline */}
          <div style={{ display: 'flex', gap: 2, marginTop: '0.5rem', alignItems: 'flex-end', height: 8 }}>
            {[2, 4, 3, 6, 5, 4, 7].map((h, j) => (
              <div key={j} style={{ flex: 1, height: h, background: j < 5 ? `${k.color}${Math.round(18 + j * 12).toString(16)}` : 'rgba(255,255,255,0.03)' }} />
            ))}
          </div>
          <div style={{ position: 'absolute', bottom: -16, right: -16, width: 50, height: 50, background: `radial-gradient(circle, ${k.color}06, transparent 70%)`, pointerEvents: 'none' }} />
        </motion.div>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   NAV CARD
───────────────────────────────────────────────────────────── */
const NavCard = ({ icon, label, desc, color, to, navigate, delay = 0, badge = 0 }) => {
  const [hov, setHov] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onClick={() => navigate(to)}
      style={{ position: 'relative', overflow: 'hidden', cursor: 'pointer', padding: '1rem', background: hov ? `${color}06` : T.bgCard, border: `1px solid ${hov ? `${color}30` : T.border}`, borderRadius: 8, transition: 'all 0.25s', transform: hov ? 'translateY(-4px)' : 'none', boxShadow: hov ? `0 12px 32px rgba(0,0,0,0.3), 0 0 0 1px ${color}14` : 'none' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`, pointerEvents: 'none' }} />
      {hov && <CardScanLine color={color} />}
      <HudCorners color1={`${color}${hov ? '60' : '30'}`} color2={`${color}${hov ? '60' : '30'}`} size={6} />

      {badge > 0 && (
        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 20, width: 16, height: 16, borderRadius: '50%', background: T.green, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#000', boxShadow: `0 0 8px ${T.green}55` }}>
          {badge}
        </div>
      )}

      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to bottom, ${color}06, transparent)`, opacity: hov ? 1 : 0, transition: 'opacity 0.3s', pointerEvents: 'none' }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
          <div style={{ width: 28, height: 28, border: `1px solid ${color}22`, background: `${color}0a`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, position: 'relative' }}>
            <HudCorners color1={`${color}40`} color2={`${color}40`} size={4} />
            {icon}
          </div>
        </div>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700, color: hov ? color : T.text, marginBottom: '0.25rem', letterSpacing: '0.04em', transition: 'color 0.2s', textTransform: 'uppercase' }}>{label}</div>
        <Mono size={9} color={T.textMute}>{desc}</Mono>
        <div style={{ marginTop: '0.85rem', paddingTop: '0.6rem', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Mono size={9} color={hov ? color : `${color}`} style={{ letterSpacing: '0.18em', textTransform: 'uppercase', transition: 'color 0.2s' }}>CONSULTER</Mono>
          <ChevronRight size={11} strokeWidth={1.5} style={{ color: hov ? color : `${color}50`, transition: 'color 0.2s' }} />
        </div>
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────────────────────
   CENTRE D'ÉTUDES DE CAS
───────────────────────────────────────────────────────────── */
const CentreCard = ({ navigate, userPlan }) => {
  const [hov, setHov] = useState(false);
  const isPaid = ['academia', 'pro', 'entreprise'].includes(userPlan);
  const color = T.accentG;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onClick={() => navigate('/centre-ajustement')}
      style={{ position: 'relative', overflow: 'hidden', cursor: 'pointer', borderRadius: 8, background: hov ? `${color}07` : T.bgCard, border: `1px solid ${hov ? `${color}35` : T.border}`, transition: 'all 0.28s', transform: hov ? 'translateY(-4px)' : 'none', boxShadow: hov ? `0 16px 40px rgba(0,0,0,0.4), 0 0 0 1px ${color}14` : 'none' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`, pointerEvents: 'none' }} />
      {hov && <CardScanLine color={color} />}
      <HudCorners color1={`${color}${hov ? '70' : '35'}`} color2={`${T.cyan}${hov ? '55' : '25'}`} size={7} />

      <div style={{ position: 'absolute', bottom: -40, right: -40, width: 140, height: 140, borderRadius: '50%', border: `1px solid ${color}08`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -15, right: -15, width: 80, height: 80, borderRadius: '50%', border: `1px solid ${color}12`, pointerEvents: 'none' }} />

      <div style={{ padding: '1.1rem 1.2rem', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{ width: 30, height: 30, border: `1px solid ${color}28`, background: `${color}07`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, position: 'relative' }}>
              <HudCorners color1={`${color}50`} color2={`${color}50`} size={4} />
              <Sparkles size={13} strokeWidth={1.5} />
            </div>
            <div>
              <Mono size={8} color={T.textMute} style={{ display: 'block', letterSpacing: '0.2em', textTransform: 'uppercase' }}>Protocole Simulation</Mono>
              <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.25rem' }}>
                {isPaid && <div style={{ padding: '0.1rem 0.4rem', background: `${color}12`, border: `1px solid ${color}22` }}><Mono size={8} color={color}>{userPlan}</Mono></div>}
                <div style={{ padding: '0.1rem 0.4rem', background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}` }}><Mono size={8} color={T.textMute}>15–30 min</Mono></div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ fontFamily: "'Georgia',serif", fontWeight: 700, fontSize: '1.05rem', letterSpacing: '-0.01em', color: T.text, marginBottom: '0.5rem' }}>
          Études <span style={{ background: `linear-gradient(135deg, ${T.accentG}, ${T.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>de Cas</span>
        </div>
        <Mono size={9} color={T.textMute} style={{ lineHeight: 1.7, display: 'block', marginBottom: '0.85rem' }}>
          Simulez une situation réelle, activez des leviers et obtenez un débrief personnalisé.
        </Mono>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.9rem' }}>
          {[
            { label: 'Diagnostic Situationnel', color: T.accentG },
            { label: 'Analyse Leviers Action',  color: T.cyan   },
            { label: 'Rapport Final Perso',      color: T.green  },
          ].map(({ label, color: c }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: c, boxShadow: `0 0 6px ${c}70`, flexShrink: 0 }} />
              <Mono size={9} color="rgba(255,255,255,0.42)">{label}</Mono>
            </div>
          ))}
        </div>

        <div style={{ paddingTop: '0.75rem', borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Mono size={9} color={hov ? color : `${color}`} style={{ letterSpacing: '0.18em', textTransform: 'uppercase', transition: 'color 0.2s' }}>CONSULTER</Mono>
          <ChevronRight size={11} strokeWidth={1.5} style={{ color: hov ? color : `${color}40`, transition: 'color 0.2s' }} />
        </div>
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────────────────────
   PARCOURS CARD
───────────────────────────────────────────────────────────── */
const ParcoursCard = ({ enrolledParcours, navigate, handleGoToWorkspace }) => {
  const [hov, setHov] = useState(false);
  const hasItems = (enrolledParcours || []).length > 0;
  const color = T.cyan;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ position: 'relative', overflow: 'hidden', background: T.bgCard, border: `1px solid ${hov ? `${color}25` : T.border}`, borderRadius: 8, transition: 'all 0.22s' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`, pointerEvents: 'none' }} />
      <HudCorners color1={`${color}45`} color2={`${T.accent}35`} size={6} />

      {/* Header */}
      <div style={{ padding: '0.9rem 1rem 0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${T.border}`, position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div style={{ width: 26, height: 26, border: `1px solid ${color}22`, background: `${color}06`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, position: 'relative' }}>
            <HudCorners color1={`${color}40`} color2={`${color}40`} size={3} />
            <Database size={11} strokeWidth={1.5} />
          </div>
          <div>
            <Mono size={8} color={T.textMute} style={{ display: 'block', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Parcours Actifs</Mono>
            <Mono size={10} color={T.text} w>Mes Formations</Mono>
          </div>
        </div>
        <button onClick={() => navigate('/parcours')} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <Mono size={8} color={T.accentG} style={{ letterSpacing: '0.15em', textTransform: 'uppercase' }}>Découvrir</Mono>
          <ArrowRight size={9} style={{ color: T.accentG }} />
        </button>
      </div>

      {/* Items */}
      <div style={{ padding: '0.5rem 0.75rem 0.65rem', display: 'flex', flexDirection: 'column', gap: '0.3rem', position: 'relative', zIndex: 1 }}>
        {hasItems
          ? (enrolledParcours || []).slice(0, 4).map((p, i) => (
            <ParcoursItem key={i} p={p} index={i} onGo={() => handleGoToWorkspace(p)} />
          ))
          : (
            <div onClick={() => navigate('/parcours')} style={{ padding: '1.5rem', textAlign: 'center', border: `1px dashed ${T.border}`, cursor: 'pointer', borderRadius: 6, transition: 'border-color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = `${color}30`}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
              <Plus size={13} style={{ color: T.textMute, display: 'block', margin: '0 auto 6px' }} />
              <Mono size={9} color={T.textMute} style={{ display: 'block', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Aucun Parcours</Mono>
              <Mono size={8} color={T.textMute} style={{ display: 'block', marginTop: '0.2rem' }}>En attente de signal...</Mono>
            </div>
          )}
      </div>

      {/* Footer */}
      <div style={{ padding: '0.6rem 1rem', borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
        <Mono size={8} color={hov ? color : `${color}`} style={{ letterSpacing: '0.18em', textTransform: 'uppercase', transition: 'color 0.2s' }}>CONSULTER</Mono>
        <ChevronRight size={10} strokeWidth={1.5} style={{ color: hov ? color : `${color}35`, transition: 'color 0.2s' }} />
      </div>
    </motion.div>
  );
};

const ParcoursItem = ({ p, index, onGo }) => {
  const pct  = p.progressionGlobale || 0;
  const name = p.parcoursId?.nom || p.nom || 'Parcours';
  const slug = (p.parcoursId?.slug || p.slug || p.id?.slice(-6) || `${index}`).slice(-6).toUpperCase();
  const done   = pct >= 100;
  const active = pct > 0 && pct < 100;
  const color  = done ? T.green : active ? T.accentG : T.textMute;

  return (
    <motion.div whileHover={{ x: 2 }} onClick={onGo}
      style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.7rem', cursor: 'pointer', border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.01)', borderRadius: 6, transition: 'all 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${T.cyan}25`; e.currentTarget.style.background = `${T.cyan}02`; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = 'rgba(255,255,255,0.01)'; }}>
      <div style={{ flexShrink: 0, width: 22, height: 22, border: `1px solid ${color}28`, background: `${color}08`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
        {done ? <CheckCircle size={9} /> : active ? <Activity size={9} /> : <Clock size={9} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', marginBottom: '0.3rem' }}>
          <Mono size={8} color={`${T.accentG}70`}>REF:{slug}</Mono>
          <Mono size={9} color={T.text} w style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{name}</Mono>
        </div>
        <div style={{ height: 1, background: 'rgba(255,255,255,0.04)', overflow: 'hidden' }}>
          <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1 }}
            style={{ height: '100%', background: color, boxShadow: active ? `0 0 4px ${color}70` : 'none' }} />
        </div>
        <Mono size={8} color={T.textMute} style={{ display: 'block', marginTop: '0.2rem' }}>{pct}% complété</Mono>
      </div>
      <ChevronRight size={9} style={{ color: T.textMute, flexShrink: 0 }} />
    </motion.div>
  );
};

/* ─────────────────────────────────────────────────────────────
   PLAN CARD
───────────────────────────────────────────────────────────── */
const PlanCard = ({ user, enrolledParcours }) => {
  const plan   = user?.subscription?.plan || 'freemium';
  const online = user?.subscription?.status === 'active';
  const used   = enrolledParcours?.length || 0;
  const max    = user?.limits?.maxParcours || 3;
  const pct    = Math.min(100, (used / max) * 100);
  const color  = { freemium: T.textMute, academia: T.cyan, pro: T.accentG }[plan] || T.textMute;

  return (
    <div style={{ position: 'relative', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: '0.9rem 1rem', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, ${color}55, transparent)` }} />
      <HudCorners color1={`${color}35`} color2={`${T.accent}25`} size={5} />

      {/* Label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.75rem' }}>
        <span style={{ width: 1, height: 10, background: `linear-gradient(to bottom, ${T.cyan}, ${T.accent})` }} />
        <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>Abonnement</Mono>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <div style={{ fontFamily: "'Georgia',serif", fontWeight: 700, fontSize: '1rem', color: T.text }}>{plan}</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.15rem 0.5rem', border: `1px solid ${online ? `${T.green}30` : T.border}`, background: online ? `${T.green}06` : 'rgba(255,255,255,0.02)' }}>
          <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.8, repeat: Infinity }}
            style={{ width: 4, height: 4, borderRadius: '50%', background: online ? T.green : T.textMute, display: 'inline-block' }} />
          <Mono size={8} color={online ? T.green : T.textMute}>{online ? 'Online' : 'Offline'}</Mono>
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
          <Mono size={8} color={T.textMute}>Parcours Slots</Mono>
          <Mono size={8} color={pct >= 80 ? T.danger : color} w>{used}/{max}</Mono>
        </div>
        <div style={{ height: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 1, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: pct >= 80 ? T.danger : color, transition: 'width 0.8s', boxShadow: `0 0 6px ${pct >= 80 ? T.danger : color}55`, borderRadius: 1 }} />
        </div>
      </div>
      {user?.subscription?.endDate && (
        <Mono size={8} color={T.textMute} style={{ display: 'block', marginTop: '0.4rem' }}>
          Exp: {new Date(user.subscription.endDate).toLocaleDateString('fr-FR')}
        </Mono>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   ACTIVITÉ RÉCENTE
───────────────────────────────────────────────────────────── */
const RecentActivity = ({ enrolledParcours }) => {
  const items = useMemo(() => {
    const arr = [];
    (enrolledParcours || []).forEach(p => {
      (p.moduleProgress || []).filter(m => m.status === 'completed').forEach(m => {
        arr.push({ name: m.nom || 'Module', parcours: p.parcoursId?.nom || p.nom || '—', score: m.score, date: m.completedAt });
      });
    });
    return arr.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);
  }, [enrolledParcours]);

  if (!items.length) return (
    <div style={{ padding: '1.5rem 0', textAlign: 'center' }}>
      <Mono size={9} color={T.textMute} style={{ letterSpacing: '0.15em' }}>Aucune activité · En attente de signal...</Mono>
    </div>
  );

  const sc = s => s >= 70 ? T.green : s >= 50 ? T.accentG : T.danger;
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {items.map((m, i) => (
        <motion.div key={i} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
          style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.4rem', borderBottom: i < items.length - 1 ? `1px solid ${T.border}` : 'none', transition: 'background 0.15s', borderRadius: 4 }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.015)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <span style={{ width: 4, height: 4, borderRadius: '50%', background: T.green, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Mono size={8} color={T.textMute} style={{ display: 'block' }}>{m.parcours}</Mono>
            <Mono size={9} color={T.text} w style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.name}</Mono>
          </div>
          {m.score !== undefined && <Mono size={10} color={sc(m.score)} w>{m.score}%</Mono>}
        </motion.div>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   CERTIFICATIONS
───────────────────────────────────────────────────────────── */
const Certs = ({ certs }) => {
  if (!certs.length) return (
    <div style={{ padding: '1.25rem', textAlign: 'center', border: `1px dashed ${T.border}`, borderRadius: 6 }}>
      <Trophy size={13} strokeWidth={1} style={{ color: T.textMute, display: 'block', margin: '0 auto 6px' }} />
      <Mono size={9} color={T.textMute} style={{ display: 'block', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Pas encore de certificats</Mono>
      <Mono size={8} color={T.textMute} style={{ display: 'block', marginTop: '0.2rem' }}>En attente complétion...</Mono>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
      {certs.slice(0, 3).map((c, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.7rem', background: `${T.accentG}05`, border: `1px solid ${T.accentG}14`, borderRadius: 7 }}>
          <Trophy size={10} style={{ color: T.accentG, flexShrink: 0 }} strokeWidth={1.5} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Mono size={9} color={T.text} w style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', textTransform: 'uppercase' }}>
              {c.title || c.parcoursName || 'Certificat'}
            </Mono>
            <Mono size={8} color={T.textMute}>{c.issuedAt ? new Date(c.issuedAt).toLocaleDateString('fr-FR') : '—'}</Mono>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   SECTION LABEL
───────────────────────────────────────────────────────────── */
const SectionLabel = ({ label, action, onAction }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
    <span style={{ width: 1, height: 10, background: `linear-gradient(to bottom, ${T.cyan}, ${T.accent})`, flexShrink: 0 }} />
    <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>{label}</Mono>
    <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, ${T.cyan}25, transparent)` }} />
    {action && (
      <button onClick={onAction} style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
        <Mono size={8} color={T.accentG} style={{ letterSpacing: '0.15em', textTransform: 'uppercase' }}>{action}</Mono>
        <ArrowRight size={8} style={{ color: T.accentG }} />
      </button>
    )}
  </div>
);

/* ─────────────────────────────────────────────────────────────
   SIDEBAR
───────────────────────────────────────────────────────────── */
const Sidebar = ({ user, userPlan, pesaCount, navigate, logout, onClose, isMobileOpen }) => {
  const NAV = [
    { icon: <Sparkles  size={12} strokeWidth={1.5} />, label: "Cas d'études", to: '/centre-intro', color: T.accentG },
    { icon: <Database  size={12} strokeWidth={1.5} />, label: 'Parcours',      to: '/parcours',    color: T.cyan   },
    { icon: <BookOpen  size={12} strokeWidth={1.5} />, label: 'Ateliers',      to: '/ateliers',    color: T.textMute },
    { icon: <Gamepad   size={12} strokeWidth={1.5} />, label: 'Expériences',   to: '/pesa-exp',    color: T.textMute },
  ];

  return (
    <motion.aside initial={{ x: -220 }} animate={{ x: 0 }} exit={{ x: -220 }} transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', background: `${T.bgPanel}fa`, backdropFilter: 'blur(24px)', borderRight: `1px solid ${T.border}`, position: isMobileOpen ? 'fixed' : 'relative', inset: isMobileOpen ? '0 auto 0 0' : 'auto', zIndex: 50, height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>

      {/* Logo */}
      <div style={{ padding: '1rem 0.9rem', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div style={{ position: 'relative', width: 28, height: 28, border: `1px solid ${T.accentG}28`, background: `${T.accentG}05`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HudCorners color1={`${T.accentG}55`} color2={`${T.cyan}40`} size={4} />
            <img src="/logo2026.png" alt="P" style={{ width: 16, objectFit: 'contain' }} />
          </div>
          <div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', color: T.text, lineHeight: 1 }}>
              PESA<span style={{ background: `linear-gradient(90deg,${T.orange},${T.accentG})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>CORE</span>
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '0.6rem 0.5rem', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Dashboard actif */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.65rem', borderLeft: `2px solid ${T.cyan}`, background: `${T.cyan}05`, marginBottom: 2 }}>
          <TrendingUp size={12} strokeWidth={1.5} style={{ color: T.cyan, flexShrink: 0 }} />
          <div>
            <Mono size={8} color={`${T.cyan}50`} style={{ display: 'block', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Vue</Mono>
            <Mono size={10} color={T.cyan} w>Dashboard</Mono>
          </div>
        </div>

        {NAV.map(({ icon, label, to, color }) => (
          <button key={label} onClick={() => { if (to) { navigate(to); if (onClose) onClose(); } }}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.65rem', border: 'none', background: 'none', cursor: 'pointer', width: '100%', transition: 'background 0.15s', textAlign: 'left', borderRadius: 4, minHeight: 48, }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.025)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <span style={{ color, flexShrink: 0 }}>{icon}</span>
            <Mono size={10} color={T.textMute}>{label}</Mono>
          </button>
        ))}
      </nav>

      {/* Footer */}
      <div style={{ padding: '0.75rem 0.65rem', borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
        {/* PESA tokens */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.65rem', background: `${T.accentG}05`, border: `1px solid ${T.accentG}16`, borderRadius: 6, marginBottom: '0.5rem', position: 'relative' }}>
          <HudCorners color1={`${T.accentG}35`} color2={`${T.accentG}25`} size={4} />
          <Zap size={10} style={{ color: T.accentG, flexShrink: 0 }} strokeWidth={1.5} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1 }}>{pesaCount}</div>
            <Mono size={8} color={T.textMute}>PESA Tokens</Mono>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 8 }}>
            {[2, 4, 3, 5, 6].map((h, i) => (
              <div key={i} style={{ width: 2, height: h, background: `${T.accentG}${(0.2 + i * 0.14).toFixed(2).slice(2).padStart(2, '0')}` }} />
            ))}
          </div>
        </div>

        {/* User */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
          onClick={() => { navigate('/users/me'); if (onClose) onClose(); }}>
          <Avatar user={user} size={26} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Mono size={10} color={T.text} w style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
              {user?.prenom} {user?.nom}
            </Mono>
            <Mono size={8} color={T.textMute}>{userPlan}</Mono>
          </div>
          <button onClick={e => { e.stopPropagation(); logout(); }} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: T.textMute, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = T.danger}
            onMouseLeave={e => e.currentTarget.style.color = T.textMute}>
            <LogOut size={10} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </motion.aside>
  );
};



/* ─────────────────────────────────────────────────────────────
   MAIN DASHBOARD
───────────────────────────────────────────────────────────── */
const DashboardLab = () => {
  const navigate  = useNavigate();
  const { user, logout, loading, refreshUser, setProfile } = useAuth();
  const { cache } = useDashboard();
  const { initParcours, inventory } = usePesaParcours();
  const { enrolledParcours } = useUser();

  const [pesaCount, setPesaCount]  = useState(user?.pesas || 0);
  
  const [msgOpen, setMsgOpen]      = useState(false);
  const [notifications, setNotifs] = useState(user?.notifications || []);
  const { unlockedIds } = useAtelierUnlock();
  const isMobile = useIsMobile(900);
  const [sideOpen, setSideOpen] = useState(true);

  useEffect(() => {
    setSideOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (!loading && user?.role === 'particulier') {
      const plan = user?.subscription?.plan;
      if (plan === 'freemium') {
        refreshUser().then(u => { if (u?.subscription?.plan && u.subscription.plan !== 'freemium') setProfile(u); }).catch(() => {});
      }
    }
  }, [loading, user, refreshUser, setProfile]);

  useEffect(() => {
    parcoursService.getActiveParcours()
      .then(d => { if (d?.course) initParcours(d.course, d.progress || {}); })
      .catch(() => {});
  }, [initParcours]);

  useEffect(() => { if (inventory?.pesaCount) setPesaCount(inventory.pesaCount); }, [inventory]);
  

  useEffect(() => {
    if (loading || !user?.role) return;
    if (['admin', 'entreprise', 'collectivité', 'établissement'].includes(user.role.toLowerCase())) {
      navigate('/admin', { replace: true });
    }
  }, [loading, user, navigate]);

  const handleMarkRead = useCallback(async id => {
    setNotifs(p => p.map(n => n.id === id ? { ...n, read: true } : n));
    try { await api.patch(`/users/me/notifications/${id}/read`); } catch {}
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    setNotifs(p => p.map(n => ({ ...n, read: true })));
    try { await api.patch('/users/me/notifications/read-all'); } catch {}
  }, []);

  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);
  const totalBadge  = useMessagrieBadge({ userId: user?.id, legacyUnreadCount: unreadCount });

  const stats = useMemo(() => {
    const modulesCompleted = (enrolledParcours || []).reduce((t, p) =>
      t + (p.moduleProgress || []).filter(m => m.status === 'completed').length, 0);
    return { modulesCompleted, xpTotal: modulesCompleted * 120, globalProgress: user?.globalProgress || 0, confiance: user?.scoreConfiance || 0, pesas: user?.pesas || 0, certificates: cache.certificates?.length || 0 };
  }, [enrolledParcours, user, cache.certificates]);

  const handleGoToWorkspace = p => {
    const slug = p?.parcoursId?.slug || p?.slug;
    navigate(slug ? `/investigation/${slug}` : '/parcours');
  };

  const userPlan     = user?.subscription?.plan ?? 'freemium';
  const certificates = cache.certificates || [];

  return (
    <div style={{ display: 'flex', height: '100dvh', background: T.bg, overflow: 'hidden', fontFamily: "'DM Mono','Roboto Mono',monospace", position: 'relative' }}>

      {/* ── FOND ── */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        <CausalGrid activeColor={null} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(${T.border} 1px,transparent 1px),linear-gradient(90deg,${T.border} 1px,transparent 1px)`, backgroundSize: '52px 52px', opacity: 0.22 }} />
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 20% 20%, ${T.cyan}04 0%, transparent 40%), radial-gradient(ellipse at 80% 80%, ${T.accent}04 0%, transparent 40%)` }} />
      </div>

      {/* ── SIDEBAR ── */}
      <AnimatePresence initial={false}>
        {!isMobile && sideOpen && (
          <Sidebar
            key="desktop-sidebar"
            user={user}
            userPlan={userPlan}
            pesaCount={pesaCount}
            navigate={navigate}
            logout={logout}
            isMobileOpen={false}
            onClose={() => setSideOpen(false)}
          />
        )}
        {isMobile && sideOpen && (
          <>
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setSideOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(5,10,14,0.85)', backdropFilter: 'blur(4px)', zIndex: 40 }}
            />
            <Sidebar
              key="mobile-sidebar"
              user={user}
              userPlan={userPlan}
              pesaCount={pesaCount}
              navigate={navigate}
              logout={logout}
              isMobileOpen
              onClose={() => setSideOpen(false)}
            />
          </>
        )}
      </AnimatePresence>

      {/* ── MAIN ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', position: 'relative', zIndex: 1 }}>

        {/* ══ HEADER ══ */}
        <header style={{ flexShrink: 0, background: `${T.bgPanel}e8`, backdropFilter: 'blur(24px)', borderBottom: `1px solid ${T.border}`, position: 'relative', zIndex: 30 }}>
          <div style={{ height: 48, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {/* Menu toggle */}
              <button onClick={() => setSideOpen(s => !s)}
                style={{ width: 28, height: 28, border: `1px solid ${T.border}`, background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textMute, transition: 'all 0.15s', position: 'relative' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `${T.cyan}40`; e.currentTarget.style.color = T.cyan; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMute; }}>
                {sideOpen ? <X size={12} /> : <Menu size={12} />}
              </button>
              <span style={{ width: 1, height: 16, background: T.border }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Terminal size={11} strokeWidth={1.5} style={{ color: T.cyan }} />
                <Mono size={9} color={T.textMute} style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}>Dashboard</Mono>
                <span style={{ color: T.textMute, fontFamily: "'DM Mono',monospace", fontSize: 9 }}>/</span>
                <Mono size={9} color={T.cyan} w style={{ letterSpacing: '0.1em' }}>{user?.prenom || 'User'}</Mono>
              </div>
              {!isMobile && (
                <>
                  <span style={{ width: 1, height: 16, background: T.border }} />
                  <Mono size={9} color={`${T.accentG}`}><LiveClock /></Mono>
                  <span style={{ width: 1, height: 16, background: T.border }} />
                  <Mono size={9} color={`${T.green}`} style={{ letterSpacing: '0.18em' }}>● STABLE</Mono>
                </>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {isMobile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '0.2rem 0.55rem', border: `1px solid ${T.accentG}22`, background: `${T.accentG}06` }}>
                  <Zap size={9} style={{ color: T.accentG }} strokeWidth={1.5} />
                  <Mono size={9} color={T.accentG} w>{pesaCount}</Mono>
                </div>
              )}
              {/* Bell */}
              <button onClick={() => setMsgOpen(s => !s)}
                style={{ position: 'relative', width: 30, height: 30, border: `1px solid ${msgOpen ? `${T.accentG}30` : T.border}`, background: msgOpen ? `${T.accentG}06` : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: msgOpen ? T.accentG : T.textMute, transition: 'all 0.15s' }}>
                <Bell size={12} strokeWidth={1.5} />
                {totalBadge > 0 && (
                  <span style={{ position: 'absolute', top: -3, right: -3, width: 13, height: 13, background: T.accentG, fontSize: 8, fontWeight: 700, color: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Mono',monospace", boxShadow: `0 0 8px ${T.accentG}55` }}>
                    {totalBadge > 9 ? '9+' : totalBadge}
                  </span>
                )}
              </button>
              
            </div>
          </div>
        </header>

        {/* ══ CONTENT ══ */}
        <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none' }}>
          <div style={{ padding: isMobile ? '1rem' : '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>

            {/* Hero */}
            <Hero user={user} stats={stats} />

            {/* KPI strip — 4 cols sur desktop, 2 sur mobile */}
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
              style={{ ...(isMobile ? { display: 'grid', gridTemplateColumns: '1fr 1fr' } : {}) }}>
              <KpiStrip stats={stats} />
            </motion.div>

            {/* Grille principale */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: '0.85rem' }}>

              {/* Col 1 — Nav cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                  <NavCard icon={<Database size={12} strokeWidth={1.5} />} label="Parcours" desc="Mes formations" color={T.cyan} to="/parcours" navigate={navigate} delay={0.12} />
                  <NavCard icon={<ToolCase size={12} strokeWidth={1.5} />} label="Ateliers" desc={unlockedIds > 0 ? `${unlockedIds} débloqué${unlockedIds > 1 ? 's' : ''}` : 'Mon espace'} color={unlockedIds > 0 ? T.green : T.textMute} to="/ateliers" navigate={navigate} delay={0.16} badge={unlockedIds} />
                </div>
                {/* Centre Cas */}
                <CentreCard navigate={navigate} userPlan={userPlan} />
              </div>

              {/* Col 2 — Parcours actifs + Activité */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <ParcoursCard enrolledParcours={enrolledParcours} navigate={navigate} handleGoToWorkspace={handleGoToWorkspace} />

                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}
                  style={{ position: 'relative', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: '0.9rem 1rem', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`, pointerEvents: 'none' }} />
                  <HudCorners color1={`${T.green}35`} color2={`${T.accent}25`} size={5} />
                  <SectionLabel label="Activité Récente" />
                  <RecentActivity enrolledParcours={enrolledParcours} />
                </motion.div>
              </div>

              {/* Col 3 — Plan + Certifs */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <PlanCard user={user} enrolledParcours={enrolledParcours} />

                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26 }}
                  style={{ position: 'relative', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: '0.9rem 1rem', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`, pointerEvents: 'none' }} />
                  <HudCorners color1={`${T.accentG}35`} color2={`${T.accent}25`} size={5} />
                  <SectionLabel label="Certifications" />
                  <Certs certs={certificates} />
                </motion.div>

                {/* Tokens bloc desktop */}
                {!isMobile && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                    style={{ position: 'relative', background: T.bgCard, border: `1px solid ${T.accentG}16`, borderRadius: 8, padding: '0.9rem 1rem', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, ${T.accentG}45, transparent)` }} />
                    <HudCorners color1={`${T.accentG}40`} color2={`${T.accentG}25`} size={5} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <Zap size={16} style={{ color: T.accentG }} strokeWidth={1.5} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "'Georgia',serif", fontWeight: 700, fontSize: '1.5rem', color: T.text, lineHeight: 1 }}>{pesaCount}</div>
                        <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}>PESA Tokens disponibles</Mono>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 14 }}>
                        {[3, 6, 4, 8, 7, 5, 9].map((h, i) => (
                          <div key={i} style={{ width: 3, height: h, background: `${T.accentG}${(0.15 + i * 0.1).toFixed(2).slice(2).padStart(2, '0')}`, borderRadius: 1 }} />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>

            <div style={{ height: 12 }} />
          </div>
        </main>

        {/* ══ FOOTER RAIL ══ */}
        <footer style={{ height: 38, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 1rem', borderTop: `1px solid ${T.border}`, background: `${T.bgPanel}e8`, backdropFilter: 'blur(20px)', position: 'relative', zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity }}
                style={{ width: 4, height: 4, borderRadius: '50%', background: T.green, boxShadow: `0 0 5px ${T.green}`, display: 'inline-block' }} />
              <Mono size={8} color={`${T.green}65`} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>En ligne</Mono>
            </div>
            {!isMobile && (
              <>
                <span style={{ width: 1, height: 8, background: T.border }} />
                <Mono size={8} color="rgba(255, 255, 255, 0.70)" style={{ letterSpacing: '0.15em' }}>
                  Apprendre <span style={{ color: T.accentG }}>/</span> Enseigner <span style={{ color: T.cyan }}>/</span> Transformer
                </Mono>
              </>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {!isMobile && <Mono size={8} style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}>Latence: 24ms</Mono>}
            {[['SIM', T.green], ['CAU', T.cyan], ['INV', T.accent]].map(([tag, col]) => (
              <div key={tag} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <div style={{ width: 4, height: 4, borderRadius: '50%', background: col, boxShadow: `0 0 4px ${col}` }} />
                <Mono size={8} style={{ letterSpacing: '0.12em' }}>{tag}</Mono>
              </div>
            ))}
            <div style={{ padding: '0.15rem 0.55rem', background: `${T.cyan}0d`, border: `1px solid ${T.cyan}28` }}>
              <Mono size={8} color={`${T.cyan}70`} style={{ letterSpacing: '0.15em', textTransform: 'uppercase' }}>Auth</Mono>
            </div>
          </div>
        </footer>
      </div>

      {/* ── MESSAGERIE ── */}
      <MessageriePanel isOpen={msgOpen} onClose={() => setMsgOpen(false)} darkMode userId={user?.id}
        legacyNotifications={notifications} onLegacyRead={handleMarkRead} onLegacyReadAll={handleMarkAllRead} legacyUnreadCount={unreadCount} />

      {/* ── ONBOARDING ── */}
      {!loading && user && user.hasSeenOnboarding !== true && (
        <OnboardingModal user={user} navigate={navigate} onDone={() => setProfile({ ...user, hasSeenOnboarding: true })} />
      )}

      <style>{`
        @keyframes dotpulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        main::-webkit-scrollbar { width: 4px; }
        main::-webkit-scrollbar-track { background: transparent; }
        main::-webkit-scrollbar-thumb { background: ${T.cyan}25; border-radius: 2px; }
        main::-webkit-scrollbar-thumb:hover { background: ${T.cyan}50; }
        @media (max-width: 900px) {
          .kpi-mobile { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default DashboardLab;